import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, output, signal } from '@angular/core';
import { ChevronDown, LucideIconData } from 'lucide-angular';
import { IconComponent } from '../icon/icon.component';

export interface SelectOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-select',
  imports: [IconComponent],
  templateUrl: './select.component.html',
  styleUrl: './select.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent {
  id = input.required<string>();
  label = input<string>('');
  options = input.required<SelectOption[]>();
  value = input<string>('');
  placeholder = input<string>('Seleccionar...');
  disabled = input<boolean>(false);
  error = input<string>('');

  valueChange = output<string>();

  protected readonly chevronIcon: LucideIconData = ChevronDown;
  protected isOpen = signal(false);
  protected activeIndex = signal(-1);

  private readonly host = inject(ElementRef<HTMLElement>);

  protected selectedLabel = computed(
    () => this.options().find(o => o.value === this.value())?.label ?? ''
  );

  protected labelId = computed(() => `${this.id()}-label`);

  protected toggleOpen(): void {
    this.isOpen() ? this.close() : this.openDropdown();
  }

  protected openDropdown(): void {
    const currentIdx = this.options().findIndex(o => o.value === this.value());
    this.activeIndex.set(currentIdx >= 0 ? currentIdx : 0);
    this.isOpen.set(true);
  }

  protected close(): void {
    this.isOpen.set(false);
    this.activeIndex.set(-1);
  }

  protected selectOption(value: string): void {
    this.valueChange.emit(value);
    this.close();
  }

  protected onFocusOut(event: FocusEvent): void {
    const related = event.relatedTarget as Node | null;
    if (!related || !this.host.nativeElement.contains(related)) {
      this.close();
    }
  }

  protected onTriggerKeydown(event: KeyboardEvent): void {
    const opts = this.options();
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.isOpen()) {
          const idx = this.activeIndex();
          if (idx >= 0 && idx < opts.length) this.selectOption(opts[idx].value);
        } else {
          this.openDropdown();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen()) {
          this.openDropdown();
        } else {
          this.activeIndex.update(i => Math.min(i + 1, opts.length - 1));
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.isOpen()) this.activeIndex.update(i => Math.max(i - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        if (this.isOpen()) this.activeIndex.set(0);
        break;
      case 'End':
        event.preventDefault();
        if (this.isOpen()) this.activeIndex.set(opts.length - 1);
        break;
      case 'Escape':
      case 'Tab':
        this.close();
        break;
    }
  }
}
